import { useState } from 'react'
import { Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type ResourceProfile, type ShiftAssignmentPayload } from '@/api/resourceCenterApi'
import { useShiftDefinitions, useShiftPatterns } from '@/hooks/useResourceCenter'
import { sundayOf, todayIso } from '@/lib/resourceCalendar'
import { weekdaysLabel } from '@/lib/shiftHours'
import { DateInput, ErrorLine, Field, Segmented, longDate, useProfileWrite } from './shared'

type Kind = ShiftAssignmentPayload['type']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * "+ New shift" (slides 10 and 11). Fixed: the same shift on the same
 * weekdays. Pattern: a rotation from the library, counted from the Sunday of
 * the start week. Variable: a shift (or OFF) per date, set afterwards on the
 * Shift tab. Starting a new one closes the running one the day before.
 */
export function ShiftAssignmentDialog({
  profile,
  open,
  onOpenChange,
}: {
  profile: ResourceProfile
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const shifts = useShiftDefinitions()
  const patterns = useShiftPatterns()
  const current = profile.shift
  const [kind, setKind] = useState<Kind>(current?.type ?? 'FIXED')
  const [shiftId, setShiftId] = useState(current?.shift?.id ?? '')
  const [weekdays, setWeekdays] = useState<number[]>(current?.type === 'FIXED' ? current.weekdays : [1, 2, 3, 4, 5])
  const [patternId, setPatternId] = useState(current?.pattern?.id ?? '')
  const [from, setFrom] = useState(todayIso())
  const write = useProfileWrite()

  const activeShifts = (shifts.data ?? []).filter(s => s.active)
  // `shiftId` starts from the PROFILE's current shift, which may since have
  // been retired or deactivated — and while shifts are still loading,
  // `activeShifts` is empty regardless. Either way the Select has nothing to
  // show it as selected, so treat it as unselected rather than letting a
  // shift the operator cannot see get submitted unchanged.
  const pickedShiftId = activeShifts.some(s => s.id === shiftId) ? shiftId : ''
  // A Fixed shift is worked only on weekdays its definition allows (z8nrz7f7g8).
  const allowed = activeShifts.find(s => s.id === pickedShiftId)?.weekdays ?? [0, 1, 2, 3, 4, 5, 6]
  const chooseShift = (id: string) => {
    setShiftId(id)
    const days = activeShifts.find(s => s.id === id)?.weekdays
    if (days) setWeekdays(w => w.filter(d => days.includes(d)))
  }
  const activePatterns = (patterns.data ?? []).filter(p => p.active)
  const ready =
    !!from &&
    (kind === 'VARIABLE' || (kind === 'FIXED' ? !!pickedShiftId && weekdays.length > 0 : !!patternId))

  const start = () => {
    const body: ShiftAssignmentPayload =
      kind === 'FIXED'
        ? { type: 'FIXED', effectiveFrom: from, shiftId: pickedShiftId, weekdays }
        : kind === 'PATTERN'
          ? { type: 'PATTERN', effectiveFrom: from, patternId }
          : { type: 'VARIABLE', effectiveFrom: from }
    write.run(() => resourceProfilesApi.addShiftAssignment(profile.userId, body), () => onOpenChange(false))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2.5'>
            <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <Clock className='h-4 w-4' />
            </span>
            New shift for {profile.name}
          </DialogTitle>
          <DialogDescription>Starting a new one closes the running one the day before.</DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <Segmented
            ariaLabel='Shift type'
            value={kind}
            onChange={setKind}
            options={[
              { value: 'FIXED', label: 'Fixed' },
              { value: 'PATTERN', label: 'Pattern' },
              { value: 'VARIABLE', label: 'Variable' },
            ]}
          />
          <ul className='space-y-1 rounded-lg bg-muted/40 p-2.5 text-xs text-muted-foreground'>
            <li>
              <span className='font-medium text-foreground'>Fixed</span> — the same shift, on the same weekdays, every
              week.
            </li>
            <li>
              <span className='font-medium text-foreground'>Pattern</span> — a rotation from the pattern library.
            </li>
            <li>
              <span className='font-medium text-foreground'>Variable</span> — a shift (or OFF) chosen day by day.
            </li>
          </ul>

          {kind === 'FIXED' && (
            <div className='space-y-2'>
              <Field label='Shift'>
                <Select value={pickedShiftId} onValueChange={chooseShift}>
                  <SelectTrigger className='h-9 text-sm' aria-label='Shift'>
                    <SelectValue placeholder='Choose a shift' />
                  </SelectTrigger>
                  <SelectContent>
                    {activeShifts.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.code} · {s.name} · {s.startTime}–{s.endTime} ({s.hours} h net) · {weekdaysLabel(s.weekdays)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div role='group' aria-label='Weekdays' className='flex gap-1'>
                {WEEKDAYS.map((d, i) => {
                  const on = weekdays.includes(i)
                  const off = !allowed.includes(i)
                  return (
                    <button
                      key={d}
                      type='button'
                      aria-pressed={on}
                      disabled={off}
                      title={off ? 'This shift is not worked on this day' : undefined}
                      onClick={() => setWeekdays(on ? weekdays.filter(x => x !== i) : [...weekdays, i].sort((a, b) => a - b))}
                      className={cn(
                        'h-9 flex-1 rounded-md border text-xs font-medium transition-colors',
                        on ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground',
                        off && 'cursor-not-allowed opacity-40',
                      )}
                    >
                      {d}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {kind === 'PATTERN' && (
            <Field label='Pattern'>
              <Select value={patternId} onValueChange={setPatternId}>
                <SelectTrigger className='h-9 text-sm' aria-label='Pattern'>
                  <SelectValue placeholder='Choose a rotation' />
                </SelectTrigger>
                <SelectContent>
                  {activePatterns.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {p.cycleDays} days · {p.averageWeeklyHours} h/week
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {kind === 'VARIABLE' && (
            <p className='text-xs text-muted-foreground'>
              Start it, then set each day on the Shift tab. Days left unset are OFF.
            </p>
          )}

          <Field label='Starting'>
            <DateInput value={from} onChange={setFrom} ariaLabel='Shift starts on' />
          </Field>
          {kind === 'PATTERN' && from && (
            <p className='text-xs text-muted-foreground'>
              The cycle counts from Sunday {longDate(sundayOf(from))}; days before {longDate(from)} show "—".
            </p>
          )}
          <ErrorLine message={write.error} />
        </div>

        <DialogFooter>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!ready || write.pending} onClick={start}>
            Start shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
